import type { PayloadAction } from '@reduxjs/toolkit';
import { isAnyOf } from '@reduxjs/toolkit';
import {
  findNodes,
  getNodeInTree,
  updateNodeInTree,
  withSearchResults,
} from '../../../components/Tree/helpers/helpers';
import { isItemNo } from '../../../utils/data/utils';
import { extend } from '../../../utils/object/object';
import {
  expandNode,
  pasteNode,
  selectSearchFocusForCatalog,
  selectTreeDataByCatalogId,
  updateData,
} from './index';
import { clearSearch as clearTreeSearch } from './helpers';
import { getCatalogId, makeTreeId } from '../../../utils/tree/ids';
import {
  customSearchMethod,
  SEARCH_BY,
  toSearchQuery,
} from '../../../utils/tree/search';
import {
  destructId,
  getNodeDataId,
  getPathById,
} from '../../../utils/tree/treeNode';
import {
  NODE_TYPE_CATEGORY,
  NODE_TYPE_ITEM,
  NODE_TYPE_MULTI_SELECT,
} from '../../../utils/types/nodeTypes';
import { fetchChildNodes } from '../../actions/nodes/fetchChildNodes';
import { clearPopulateTreeState } from '../../actions/nodes/prePopulateTreeState';
import { selectNode } from '../../actions/nodes/selectNode';
import { deleteCategories } from '../../actions/tree/deleteCategories';
import { removeTreeControlsOfType } from '../../actions/tree/removeTreeControls';
import {
  searchItemAndAddToCatalog,
  searchNonSearchableCatalogs,
} from '../../actions/tree/search';
import { treeRemoveItems } from '../../actions/tree/updates';
import { selectSearchableCatalogIds } from '../../selectors/catalog/ids';
import { selectedTreeNodeSelector } from '../../selectors/tree/node';
import { selectPrestate } from '../prestate';
import {
  CATALOG_TYPE_NOTVALIDFORSTRUCTURE,
  CATALOG_TYPE_UNCATEGORIZED,
} from '../../../utils/types/catalogTypes';
import { selectNodes } from '../treeSelection';
import {
  search,
  searchBySuggestionId,
  searchUpdate,
  clearSearch,
  moveSearchNext,
  moveSearchPrevious,
  toggleSearchPanel,
  setSearchCondition,
  setSearchResults,
  selectSearchConditionForTree,
} from '../treeSearch';
import type { RootState } from '../..';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';

interface ListenerApi {
  dispatch: (action: unknown) => unknown;
  getState: () => RootState;
}

interface ListenerApi {
  dispatch: (action: unknown) => unknown;
  getState: () => RootState;
}

export const treeSearchListenerOptions = {
  actionCreator: search,
  effect: (
    action: PayloadAction<unknown, string, { tree: string }>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const state = getState();
    const {
      meta: { tree },
    } = action;
    const searchCondition = selectSearchConditionForTree(state, tree);
    if (!searchCondition) return;

    const { by, condition } = searchCondition;
    const searchableCatalogIds = selectSearchableCatalogIds(state) as string[];

    const catalogId = getCatalogId(tree);
    const isItemNumber = isItemNo(condition);

    if (
      searchableCatalogIds.includes(catalogId) &&
      by === SEARCH_BY.TEXT &&
      isItemNumber
    ) {
      dispatch(searchItemAndAddToCatalog(tree, condition) as unknown);
    } else if (catalogId === CATALOG_TYPE_UNCATEGORIZED && isItemNumber) {
      dispatch(
        searchNonSearchableCatalogs(tree, condition, 'uncategorised') as unknown,
      );
    } else if (catalogId === CATALOG_TYPE_NOTVALIDFORSTRUCTURE && isItemNumber) {
      dispatch(
        searchNonSearchableCatalogs(
          tree,
          condition,
          'notValidForStructure',
        ) as unknown,
      );
    }

    // Update tree data with search results
    const treeData = selectTreeDataByCatalogId(state, catalogId);
    if (treeData && treeData.length > 0) {
      const searchQuery = toSearchQuery(searchCondition);
      const foundNodes = findNodes({
        treeData,
        searchMethod: (ni: NodeBaseItem) =>
          customSearchMethod({ node: ni.node, searchQuery }),
        searchFocusOffset: null,
        searchQuery: null,
      }) as unknown;

      const { matches } = foundNodes as { matches: NodeBaseItem[] };
      const searchFocusIndex = matches.length ? 0 : null;
      const searchFocusId = matches[0]?.node.id ?? null;

      // Update tree data with search results
      const updatedTreeData = withSearchResults({
        treeData,
        searchFocusId,
        searchMatches: matches,
        expandMatches: true,
      });

      dispatch(updateData(tree, updatedTreeData as NodeBase[]) as unknown);

      // Sync search results to treeSearch slice
      dispatch(
        setSearchResults({
          catalogId,
          searchQuery,
          searchMatches: matches,
          searchFocusIndex,
          searchFocusId,
        }),
      );
    }
  },
};

const typesWithAsyncContent = [NODE_TYPE_CATEGORY, NODE_TYPE_ITEM];

interface ExpandNodePayload {
  treeNode?: {
    node?: {
      type: string;
    };
  };
}

export const treeExpandNodeListenerOptions = {
  actionCreator: expandNode,
  effect: (
    action: PayloadAction<ExpandNodePayload, string, { tree: string }>,
    listenerApi: ListenerApi,
  ) => {
    if (typesWithAsyncContent.includes(action.payload.treeNode?.node?.type || '')) {
      const { dispatch } = listenerApi;
      const {
        meta: { tree },
        payload: { treeNode },
      } = action;
      dispatch(fetchChildNodes(tree, treeNode) as unknown);
    }
  },
};

function updateTreeState(
  { dispatch, getState }: ListenerApi,
  treeState: Record<string, string[]>,
): void {
  const [property, state] = Object.entries(treeState)[0];
  if (!property || !state) {
    return;
  }
  const destructResult = destructId(state[0]) as { catalogId?: string };
  const catalogId = destructResult.catalogId;
  if (!catalogId) return;

  const treeData = selectTreeDataByCatalogId(getState(), catalogId);
  if (
    !Array.isArray(treeData) ||
    !treeData.length ||
    !getNodeInTree({ treeData, path: getPathById(state[0]) })
  ) {
    return;
  }

  const updatedTreeData = state.reduce(
    (td: NodeBase[], nodeId: string): NodeBase[] => {
      const path = getPathById(nodeId);
      const nodeResult = getNodeInTree({ treeData, path }) as {
        node: NodeBase;
      } | null;
      if (!nodeResult) return td;

      const { node } = nodeResult;
      return updateNodeInTree({
        node: extend(node, { expanded: true }),
        treeData: td,
        path,
      }) as NodeBase[];
    },
    treeData,
  );

  dispatch(clearPopulateTreeState(property));
  dispatch(updateData(makeTreeId(null, catalogId), updatedTreeData) as unknown);
}

interface UpdateDataMeta {
  tree?: string;
  selectNodeAtPath?: string[];
}

export const treeUpdateDataListenerOptions = {
  actionCreator: updateData,
  effect: (
    action: PayloadAction<unknown, string, UpdateDataMeta>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const state = getState();
    let catalogId: string | undefined;
    let updatedNode;
    const { meta } = action;
    if (meta.selectNodeAtPath) {
      const selected = selectedTreeNodeSelector(state);
      if (
        selected.catalogId !== getCatalogId(meta.tree || '') ||
        selected.nodeItem?.path.join('#') !== meta.selectNodeAtPath.join('#')
      ) {
        return;
      }
      catalogId = selected.catalogId;
      updatedNode = getNodeInTree({
        treeData: selectTreeDataByCatalogId(state, catalogId),
        path: meta.selectNodeAtPath,
      });
    } else {
      const prestate = selectPrestate(state);
      if (prestate) {
        const { selectedNode, sourceTreeState, targetTreeState } = prestate;

        const path = selectedNode?.path;
        catalogId = selectedNode?.catalogId;
        if (path && catalogId) {
          updatedNode = getNodeInTree({
            treeData: selectTreeDataByCatalogId(state, catalogId),
            path,
          });
          if (updatedNode) {
            dispatch(clearPopulateTreeState('selectedNode'));
          }
        }
        if (Array.isArray(sourceTreeState)) {
          updateTreeState(listenerApi, { sourceTreeState });
        }
        if (Array.isArray(targetTreeState)) {
          updateTreeState(listenerApi, { targetTreeState });
        }
      }
    }
    if (updatedNode && catalogId) {
      const tree = makeTreeId(null, catalogId);
      dispatch(selectNode(tree, updatedNode, true) as unknown);
    }
  },
};

export const treeActionsListenerOptions = {
  matcher: isAnyOf(deleteCategories, treeRemoveItems, pasteNode),
  effect: (
    action: PayloadAction<unknown, string, { tree?: string }>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch } = listenerApi;
    const {
      meta: { tree },
    } = action;
    if (tree) {
      dispatch(removeTreeControlsOfType(tree, NODE_TYPE_MULTI_SELECT));
    }
  },
};

// Sync search navigation results to treeSelection
export const searchNavigationListenerOptions = {
  matcher: isAnyOf(search, searchBySuggestionId, moveSearchNext, moveSearchPrevious),
  effect: (
    action: PayloadAction<unknown, string, { tree: string }>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
    } = action;
    const catalogId = getCatalogId(tree);
    const state = getState();

    const focusedNode = selectSearchFocusForCatalog(state, catalogId);
    if (focusedNode) {
      dispatch(selectNodes(tree, focusedNode, []));
    }
  },
};

// Listener for searchBySuggestionId action
// Updates tree data with search results and syncs search state
export const treeSearchBySuggestionIdListenerOptions = {
  actionCreator: searchBySuggestionId,
  effect: (
    action: PayloadAction<
      { suggestionId: string; suggestionTitle: string },
      string,
      { tree: string }
    >,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
      payload: { suggestionId, suggestionTitle },
    } = action;
    const catalogId = getCatalogId(tree);
    const state = getState();
    const treeData = selectTreeDataByCatalogId(state, catalogId);

    if (!treeData || treeData.length === 0) {
      return;
    }

    // Find all nodes that match this data ID
    const foundNodes = findNodes({
      treeData,
      searchMethod: (ni: NodeBaseItem) => getNodeDataId(ni.node) === suggestionId,
      searchFocusOffset: null,
      searchQuery: null,
    }) as unknown;

    const { matches } = foundNodes as { matches: NodeBaseItem[] };

    if (matches.length > 0) {
      const searchFocusId = matches[0].node.id;
      const searchFocusIndex = 0;

      // Update tree data with search results
      const updatedTreeData = withSearchResults({
        treeData,
        searchFocusId,
        searchMatches: matches,
        expandMatches: true,
      });

      // Update tree data
      dispatch(updateData(tree, updatedTreeData as NodeBase[]) as unknown);

      // Sync search results to treeSearch slice
      dispatch(
        setSearchResults({
          catalogId,
          searchQuery: {
            by: SEARCH_BY.TEXT,
            condition: suggestionTitle,
            regexp: suggestionTitle,
          },
          searchMatches: matches,
          searchFocusIndex,
          searchFocusId,
        }),
      );

      // Select the focused node
      dispatch(selectNodes(tree, matches[0], []));
    }
  },
};

// Listener for searchUpdate action - updates tree data with search results
export const searchUpdateListenerOptions = {
  actionCreator: searchUpdate,
  effect: (
    action: PayloadAction<
      { catalogIdsList?: string[]; item?: unknown; structureReasonText?: string },
      string,
      { tree: string }
    >,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
    } = action;
    const catalogId = getCatalogId(tree);
    const state = getState();
    const treeData = selectTreeDataByCatalogId(state, catalogId);
    const searchCondition = selectSearchConditionForTree(state, tree);

    if (!treeData || treeData.length === 0 || !searchCondition) return;

    const searchQuery = toSearchQuery(searchCondition);
    const foundNodes = findNodes({
      treeData,
      searchMethod: (ni: NodeBaseItem) =>
        customSearchMethod({ node: ni.node, searchQuery }),
      searchFocusOffset: null,
      searchQuery: null,
    }) as unknown;

    const { matches: searchMatches } = foundNodes as { matches: NodeBaseItem[] };
    const searchFocusIndex = searchMatches.length ? 0 : null;
    const searchFocusId = searchMatches[0]?.node.id ?? null;

    const updatedTreeData = withSearchResults({
      treeData,
      searchFocusId,
      searchMatches,
      expandMatches: true,
    });

    dispatch(updateData(tree, updatedTreeData as NodeBase[]) as unknown);

    // Sync results to treeSearch slice
    dispatch(
      setSearchResults({
        catalogId,
        searchQuery: {
          ...searchQuery,
          updated: Date.now(),
          catalogIds: action.payload.catalogIdsList,
          item: action.payload.item as NodeBaseItem['node']['item'],
          structureReasonText: action.payload.structureReasonText,
        },
        searchMatches,
        searchFocusIndex,
        searchFocusId,
      }),
    );
  },
};

// Listener for moveSearchNext/moveSearchPrevious - updates tree data with new focus
export const searchMoveListenerOptions = {
  matcher: isAnyOf(moveSearchNext, moveSearchPrevious),
  effect: (
    action: PayloadAction<null, string, { tree: string }>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
    } = action;
    const catalogId = getCatalogId(tree);
    const state = getState();
    const treeData = selectTreeDataByCatalogId(state, catalogId);

    if (!treeData || treeData.length === 0) return;

    // Get the updated search focus from treeSearch slice
    const treeSearchState = state.treeSearch?.catalogs[catalogId];
    if (!treeSearchState) return;

    const { searchMatches, searchFocusId } = treeSearchState;

    const updatedTreeData = withSearchResults({
      treeData,
      searchMatches,
      searchFocusId,
      expandMatches: false,
    });

    dispatch(updateData(tree, updatedTreeData as NodeBase[]) as unknown);
  },
};

// Listener for toggleSearchPanel - clears tree search state when closing
export const toggleSearchPanelListenerOptions = {
  actionCreator: toggleSearchPanel,
  effect: (
    action: PayloadAction<{ open?: boolean }, string, { tree: string }>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
      payload: { open },
    } = action;
    const catalogId = getCatalogId(tree);

    // If closing, clear tree search state
    if (open === false) {
      const state = getState();
      const treeData = selectTreeDataByCatalogId(state, catalogId);
      if (treeData && treeData.length > 0) {
        const clearedState = clearTreeSearch(treeData);
        dispatch(updateData(tree, clearedState.treeData as NodeBase[]) as unknown);
      }
    }
  },
};

// Listener for clearSearch - clears tree search highlighting
export const clearSearchListenerOptions = {
  actionCreator: clearSearch,
  effect: (
    action: PayloadAction<null, string, { tree: string }>,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
    } = action;
    const catalogId = getCatalogId(tree);
    const state = getState();
    const treeData = selectTreeDataByCatalogId(state, catalogId);

    if (treeData && treeData.length > 0) {
      const clearedState = clearTreeSearch(treeData);
      dispatch(updateData(tree, clearedState.treeData as NodeBase[]) as unknown);
    }
  },
};

// Listener for setSearchCondition - generates suggestions
export const setSearchConditionListenerOptions = {
  actionCreator: setSearchCondition,
  effect: (
    action: PayloadAction<
      { by: string; condition?: string },
      string,
      { tree: string }
    >,
    listenerApi: ListenerApi,
  ) => {
    const { dispatch, getState } = listenerApi;
    const {
      meta: { tree },
      payload: { by, condition },
    } = action;
    const catalogId = getCatalogId(tree);
    const state = getState();
    const treeData = selectTreeDataByCatalogId(state, catalogId);

    if (!condition || !treeData || treeData.length === 0) return;

    // Import and use getFirstXSearchResults to generate suggestions
    const searchCondition = { by, condition };
    const searchQuery = toSearchQuery(searchCondition);

    // Use findNodes to generate suggestions (simplified version)
    const foundNodes = findNodes({
      treeData,
      searchMethod: (ni: NodeBaseItem) =>
        customSearchMethod({ node: ni.node, searchQuery }),
      searchFocusOffset: null,
      searchQuery: null,
    }) as unknown;

    const { matches } = foundNodes as { matches: NodeBaseItem[] };

    // Take first 8 matches as suggestions
    const suggestions = matches.slice(0, 8).map((match) => ({
      id: getNodeDataId(match.node) || match.node.id,
      title: match.node.title || '',
      type: match.node.type || '',
      data: match.node,
    }));

    dispatch({
      type: 'treeSearch/setSearchSuggestions',
      payload: { catalogId, suggestions },
    });
  },
};
